import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {device} from 'styled/device';
import {useScreenResize, usePath} from 'hooks';
import {PATHS} from 'Routes';
import {useChekinSDK} from 'context/ChekinSDK';
import {useSummary} from 'context/summary';
import {useKioskoStore} from 'context/kiosko/useKioskoStore';
import {useIVStore} from 'store/identityVerification';
import {setIVIntentRemoteSession} from 'hooks/iv/useIVRemoteSync';
import {getUserId} from 'utils/getUserId';
import {QUERY_CACHE_KEYS} from 'utils/constants';
import {getFeatureFlags} from 'utils/features';

export const useRemoteIVFlow = (isNeedContactVerification?: boolean) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {createIvIntent} = useChekinSDK();
  const {reservationId} = useSummary();
  const {isMatch: isMobile} = useScreenResize(device.mobileXL);
  const isKiosko = useKioskoStore(state => state.connection.isConnected);
  const {getFullPath, getMainPath} = usePath();
  const {resetAllIVState} = useIVStore();
  const getStartVerificationPath = useCallback(
    (isContactVerification?: boolean) => {
      if (isContactVerification) return PATHS.verification.contacts;
      if (isMobile || isKiosko || !getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE'))
        return PATHS.verification.documentSelection;
      return PATHS.verification.scanQRCode;
    },
    [isMobile, isKiosko],
  );

  const goNextAfterContactVerification = useCallback(() => {
    const path = getStartVerificationPath(false);
    navigate(`../${path}`);
  }, [navigate, getStartVerificationPath]);

  const goToDocumentSelection = useCallback(
    () => navigate(`../${PATHS.verification.documentSelection}`),
    [navigate],
  );

  const goToGuestForm = useCallback(() => {
    navigate(getFullPath(PATHS.addPersonalDataForm));
  }, [navigate, getFullPath]);

  const goToGuestRegistration = useCallback(() => {
    queryClient.invalidateQueries([QUERY_CACHE_KEYS.guestGroup]);
    navigate(getMainPath(PATHS.guestRegistration));
  }, [queryClient, navigate, getMainPath]);

  const goToHome = useCallback(() => {
    navigate(getFullPath(PATHS.home));
  }, [navigate, getFullPath]);

  const startVerification = useCallback(async () => {
    try {
      if (getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE')) {
        const ivIntent = await createIvIntent({
          id: crypto?.randomUUID(),
          reservation_id: reservationId,
          user_id: getUserId(),
        });
        setIVIntentRemoteSession(ivIntent.id);
      }
    } catch (error) {
      console.error(error);
    }

    resetAllIVState();
    navigate(getStartVerificationPath(isNeedContactVerification));
  }, [
    createIvIntent,
    getStartVerificationPath,
    navigate,
    reservationId,
    resetAllIVState,
    isNeedContactVerification,
  ]);

  return {
    startVerification,
    goToDocumentSelection,
    goToGuestForm,
    goToHome,
    goToGuestRegistration,
    goNextAfterContactVerification,
  };
};
